import { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Button,
	Box,
	Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { changeUserPassword } from "../../services/api";
import { Loading } from "../../components/common/Loading";
import { Check } from "@mui/icons-material";

export default function ChangePasswordModal({ isOpen, onClose }) {
	const { t } = useTranslation();

	const [form, setForm] = useState({
		currentPassword: "",
		newPassword: "",
		confirmPassword: "",
	});

	const [errors, setErrors] = useState({
		currentPassword: "",
		newPassword: "",
		confirmPassword: "",
	});

	const [isPending, setIsPending] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");

	useEffect(() => {
		if (isOpen) {
			setForm({
				currentPassword: "",
				newPassword: "",
				confirmPassword: "",
			});
			setErrors({
				currentPassword: "",
				newPassword: "",
				confirmPassword: "",
			});
			setIsPending(false);
			setIsSuccess(false);
			setSuccessMessage("");
		}
	}, [isOpen]);

	useEffect(() => {
		if (!isSuccess) return;
		const timer = setTimeout(() => {
			onClose();
			setIsSuccess(false);
			setSuccessMessage("");
		}, 3000);
		return () => clearTimeout(timer);
	}, [isSuccess, onClose]);

	const handleChange = (field) => (e) => {
		setForm((prev) => ({
			...prev,
			[field]: e.target.value,
		}));
	};

	const validateInputs = () => {
		let isValid = true;
		const newErrors = {
			currentPassword: "",
			newPassword: "",
			confirmPassword: "",
		};

		if (!form.currentPassword.trim()) {
			newErrors.currentPassword = t("pleaseEnterCurrentPassword");
			isValid = false;
		}

		if (!form.newPassword.trim() || form.newPassword.length < 6) {
			newErrors.newPassword = t("passwordMustBeAtLeast6Characters");
			isValid = false;
		}

		if (form.newPassword !== form.confirmPassword) {
			newErrors.confirmPassword = t("passwordsDoNotMatch");
			isValid = false;
		}

		setErrors(newErrors);
		return isValid;
	};

	const submitForm = async (e) => {
		e.preventDefault();
		if (!validateInputs()) return;

		setIsPending(true);

		try {
			const data = await changeUserPassword({
				currentPassword: form.currentPassword,
				newPassword: form.newPassword,
				confirmPassword: form.confirmPassword,
			});
			setSuccessMessage(data.message || t("passwordChangedSuccessfully"));
			setIsSuccess(true);
		} catch (err) {
			console.error(err);
		} finally {
			setIsPending(false);
		}
	};

	return (
		<Dialog open={isOpen} onClose={onClose} maxWidth="xs" fullWidth>
			{isPending && <Loading />}
			{isSuccess && successMessage && (
				<Box
					sx={{
						position: "fixed",
						top: "100px",
						left: "50%",
						transform: "translateX(-50%)",
						zIndex: 5,
						width: "auto",
					}}
				>
					<Alert
						icon={<Check fontSize="inherit" />}
						severity="success"
					>
						{successMessage}
					</Alert>
				</Box>
			)}

			<DialogTitle>{t("changePassword")}</DialogTitle>

			<form onSubmit={submitForm} noValidate>
				<DialogContent
					sx={{ display: "flex", flexDirection: "column", gap: 2 }}
				>
					<TextField
						label={t("currentPassword")}
						type="password"
						value={form.currentPassword}
						onChange={handleChange("currentPassword")}
						error={!!errors.currentPassword}
						helperText={errors.currentPassword}
					/>
					<TextField
						label={t("newPassword")}
						type="password"
						value={form.newPassword}
						onChange={handleChange("newPassword")}
						error={!!errors.newPassword}
						helperText={errors.newPassword}
					/>
					<TextField
						label={t("confirmNewPassword")}
						type="password"
						value={form.confirmPassword}
						onChange={handleChange("confirmPassword")}
						error={!!errors.confirmPassword}
						helperText={errors.confirmPassword}
					/>
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose}>{t("cancel")}</Button>
					<Button
						type="submit"
						variant="contained"
						disabled={isPending || isSuccess}
					>
						{t("save")}
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
}
